import { Link } from 'react-router-dom';
import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, HardDrive, Network,
  Clock, MapPin, TrendingUp, Database,
  ShoppingCart, Gamepad2, Cloud, DollarSign
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const GermanyPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: Clock, value: t('germany.stats.latency'), label: t('germany.stats.latencyLabel') },
    { icon: Zap, value: t('germany.stats.speed'), label: t('germany.stats.speedLabel'), wiki: 'shared-1gbps-port-explained' },
    { icon: TrendingUp, value: t('germany.stats.uptime'), label: t('germany.stats.uptimeLabel') },
    { icon: Globe, value: t('germany.stats.exchange'), label: t('germany.stats.exchangeLabel') }
  ];

  const whyGermany = [
    {
      icon: Network,
      title: t('germany.why.decix.title'),
      description: t('germany.why.decix.desc')
    },
    {
      icon: Shield,
      title: t('germany.why.dataProtection.title'),
      description: t('germany.why.dataProtection.desc')
    },
    {
      icon: MapPin,
      title: t('germany.why.centralLocation.title'),
      description: t('germany.why.centralLocation.desc')
    },
    {
      icon: DollarSign,
      title: t('germany.why.financial.title'),
      description: t('germany.why.financial.desc')
    }
  ];

  const infrastructure = [
    {
      icon: Network,
      title: t('germany.infrastructure.network.title'),
      description: t('germany.infrastructure.network.desc')
    },
    {
      icon: Database,
      title: t('germany.infrastructure.storage.title'),
      description: t('germany.infrastructure.storage.desc')
    },
    {
      icon: Zap,
      title: t('germany.infrastructure.power.title'),
      description: t('germany.infrastructure.power.desc')
    },
    {
      icon: Server,
      title: t('germany.infrastructure.cooling.title'),
      description: t('germany.infrastructure.cooling.desc')
    }
  ];

  const useCases = [
    {
      icon: ShoppingCart,
      title: t('germany.useCases.ecommerce.title'),
      description: t('germany.useCases.ecommerce.desc')
    },
    {
      icon: TrendingUp,
      title: t('germany.useCases.fintech.title'),
      description: t('germany.useCases.fintech.desc')
    },
    {
      icon: Gamepad2,
      title: t('germany.useCases.gaming.title'),
      description: t('germany.useCases.gaming.desc')
    },
    {
      icon: Cloud,
      title: t('germany.useCases.saas.title'),
      description: t('germany.useCases.saas.desc')
    }
  ];

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="germany" />
      <Header />
      
      {/* Hero Section - German Dark Orange/Gold theme */}
      <section className="relative py-20 bg-gradient-to-br from-orange-700 via-orange-600 to-yellow-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-600 rounded-full blur-3xl"></div>
        </div>
        
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600/20 border border-orange-500/30 rounded-full text-orange-50 text-sm font-semibold mb-6">
              <MapPin className="w-4 h-4" />
              <span>{t('germany.hero.badge')}</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('germany.hero.title')}
            </h1>
            <p className="text-xl text-orange-50 max-w-3xl mx-auto mb-8">
              {t('germany.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToPricing}
                className="bg-white text-orange-800 hover:bg-orange-50 font-semibold px-8 py-6 text-lg"
              >
                {t('germany.hero.getStarted')}
              </Button>
              <Button 
                onClick={scrollToPricing}
                className="bg-orange-900 text-white hover:bg-orange-950 font-semibold px-8 py-6 text-lg"
              >
                {t('germany.hero.viewPlans')}
              </Button>
            </div>
          </div>

          {/* Dusseldorf City Image - Modern Skyline */}
          <div className="rounded-2xl overflow-hidden max-w-4xl mx-auto shadow-2xl">
            <img 
              src="/images/locations/pexels_12105656.jpg" 
              alt="Dusseldorf Modern Skyline with Business District - VPS Hosting Germany"
              loading="lazy"
              className="w-full h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-xl mb-4">
                  <stat.icon className="w-8 h-8 text-orange-700" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
                {stat.wiki && (
                  <Link
                    to={(language === 'ru' ? '/ru' : '') + '/wiki/' + stat.wiki}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 transition-colors"
                  >
                    {language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'} →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Germany Section */}
      <section className="py-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('germany.why.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('germany.why.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyGermany.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 border-2 border-orange-200 hover:border-orange-600 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-orange-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Infrastructure Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('germany.infrastructure.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('germany.infrastructure.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infrastructure.map((item, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-b from-orange-50 to-white rounded-xl border border-orange-200">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-xl mb-4">
                  <item.icon className="w-8 h-8 text-orange-700" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Datacenter Image */}
          <div className="mt-12 rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/images/locations/unsplash_1558494949-ef010cbdcc31.jpg" 
              alt="Modern Datacenter Infrastructure - Germany VPS Hosting"
              loading="lazy"
              className="w-full h-80 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Related Locations - Internal Links for SEO */}
      <RelatedLocations currentLocation="vps-germany" />

      {/* Pricing Section */}
      <section id="pricing" className="pt-16 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('germany.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('germany.pricing.subtitle')}
            </p>
          </div>
        </div>
        <PricingSection hideHeader={true} />
        
        {/* Payment Methods Section */}
        <div className="pt-16">
          <PaymentMethodsSection transparent={true} />
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('germany.useCases.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('germany.useCases.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gradient-to-br from-orange-50 to-white p-6 rounded-xl border-2 border-orange-100 hover:border-orange-500 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <useCase.icon className="w-6 h-6 text-orange-700" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-gray-600 text-sm">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Details */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('germany.location.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('germany.location.subtitle')}
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('germany.location.about')}
              </p>
              <ul className="space-y-3">
                {t('germany.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-orange-700 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="/images/locations/unsplash_1635328089968-05476754557d.jpg" 
                alt="Dusseldorf Rheinturm Tower and Rhine River at Night - VPS Germany Location"
                loading="lazy"
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-700 via-orange-600 to-yellow-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('germany.cta.title')}
          </h2>
          <p className="text-xl text-orange-50 mb-8 max-w-2xl mx-auto">
            {t('germany.cta.subtitle')}
          </p>
          <Button 
            onClick={scrollToPricing}
            className="bg-white text-orange-800 hover:bg-orange-50 font-bold px-10 py-6 text-xl"
          >
            {t('germany.cta.button')}
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-germany" />
      <Footer />
    </div>
  );
};
